import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { Plus } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import {
  useApplyDomainForwardingDnsMutation,
  useCreateForwardingDestinationMutation,
  useDeleteForwardingDestinationMutation,
  useGetDomainForwardingSetupQuery,
  useGetForwardingDestinationsQuery,
  useUpdateForwardingDestinationMutation,
  useUpdateDomainForwardingMutation
} from '../services/api';

const DomainForwardingModal = ({ show, onHide, domain, onSaved }) => {
  const {
    data: forwardingDestinationsData,
    isLoading: isLoadingDestinations,
    refetch: refetchDestinations
  } = useGetForwardingDestinationsQuery(undefined, { skip: !show });
  const [createForwardingDestination, { isLoading: isCreatingDestination }] = useCreateForwardingDestinationMutation();
  const [updateForwardingDestination, { isLoading: isUpdatingDestination }] = useUpdateForwardingDestinationMutation();
  const [deleteForwardingDestination, { isLoading: isDeletingDestination }] = useDeleteForwardingDestinationMutation();
  const [updateDomainForwarding, { isLoading: isSavingForwarding }] = useUpdateDomainForwardingMutation();
  const [applyDomainForwardingDns, { isLoading: isApplyingForwardingDns }] = useApplyDomainForwardingDnsMutation();
  const {
    data: forwardingSetupData,
    isLoading: isLoadingForwardingSetup,
    refetch: refetchForwardingSetup
  } = useGetDomainForwardingSetupQuery(domain?.domain, {
    skip: !show || !domain?.domain
  });

  const [forwardingEnabled, setForwardingEnabled] = useState(false);
  const [forwardingDestinationId, setForwardingDestinationId] = useState('');
  const [forwardingPreservePath, setForwardingPreservePath] = useState(true);
  const [forwardingPreserveQuery, setForwardingPreserveQuery] = useState(true);
  const [forwardingStatusCode, setForwardingStatusCode] = useState(301);
  const [forwardingError, setForwardingError] = useState('');

  const [newDestinationName, setNewDestinationName] = useState('');
  const [newDestinationUrl, setNewDestinationUrl] = useState('');
  const [editingDestinationId, setEditingDestinationId] = useState('');
  const [deletingDestinationId, setDeletingDestinationId] = useState('');

  const destinations = useMemo(() => {
    return forwardingDestinationsData?.data?.destinations || forwardingDestinationsData?.destinations || [];
  }, [forwardingDestinationsData]);

  const selectedDestination = useMemo(() => {
    if (!forwardingDestinationId) return null;
    return destinations.find((d) => d.destinationId === forwardingDestinationId) || null;
  }, [destinations, forwardingDestinationId]);

  const forwardingSetup = useMemo(() => {
    return forwardingSetupData?.data || forwardingSetupData || null;
  }, [forwardingSetupData]);

  const redirectInfraConfigured = Boolean(forwardingSetup?.redirectInfra?.isConfigured);
  const route53AutomationEligible = Boolean(
    forwardingSetup?.automation?.route53Eligible ?? (domain?.isInRoute53 && domain?.hostedZoneId)
  );
  const canAutoApplyDns = Boolean(
    forwardingSetup?.automation?.canAutoApply ?? (route53AutomationEligible && redirectInfraConfigured)
  );
  const setupDistributionDomain = forwardingSetup?.redirectInfra?.distributionDomain || '';
  const setupApex = forwardingSetup?.dns?.apex || null;
  const setupWww = forwardingSetup?.dns?.www || null;
  const setupChecklist = Array.isArray(forwardingSetup?.verificationChecklist)
    ? forwardingSetup.verificationChecklist
    : [];

  useEffect(() => {
    if (!show || !domain) return;
    setForwardingEnabled(Boolean(domain.forwardingEnabled));
    setForwardingDestinationId(String(domain.forwardingDestinationId || ''));
    setForwardingPreservePath(domain.forwardingPreservePath !== false);
    setForwardingPreserveQuery(domain.forwardingPreserveQuery !== false);
    setForwardingStatusCode(Number(domain.forwardingStatusCode) === 302 ? 302 : 301);
    setForwardingError('');
    setEditingDestinationId('');
    setDeletingDestinationId('');
    setNewDestinationName('');
    setNewDestinationUrl('');
  }, [show, domain]);

  const handleCreateOrUpdateDestination = async () => {
    try {
      setForwardingError('');
      const name = String(newDestinationName || '').trim();
      const targetUrl = String(newDestinationUrl || '').trim();
      if (!name) {
        setForwardingError('Destination name is required.');
        return;
      }
      if (!targetUrl) {
        setForwardingError('Destination URL is required.');
        return;
      }
      if (editingDestinationId) {
        await updateForwardingDestination({
          destinationId: editingDestinationId,
          name,
          targetUrl
        }).unwrap();
        toast.success('Destination updated');
      } else {
        const response = await createForwardingDestination({ name, targetUrl }).unwrap();
        const created = response?.data?.destination || response?.destination;
        if (created?.destinationId) {
          setForwardingDestinationId(created.destinationId);
        }
        toast.success('Destination added');
      }
      setNewDestinationName('');
      setNewDestinationUrl('');
      setEditingDestinationId('');
      refetchDestinations();
      refetchForwardingSetup?.();
    } catch (error) {
      console.error('Failed to save forwarding destination:', error);
      setForwardingError(error?.data?.message || error?.message || 'Failed to save destination');
    }
  };

  const handleEditDestination = (destination) => {
    setEditingDestinationId(destination.destinationId);
    setNewDestinationName(destination.name || '');
    setNewDestinationUrl(destination.targetUrl || '');
    setForwardingError('');
  };

  const handleCancelDestinationEdit = () => {
    setEditingDestinationId('');
    setNewDestinationName('');
    setNewDestinationUrl('');
    setForwardingError('');
  };

  const handleDeleteDestination = async (destination) => {
    const confirmed = window.confirm(
      `Delete destination "${destination.name}"?\n\nDomains currently using it must be reassigned first.`
    );
    if (!confirmed) return;

    try {
      setForwardingError('');
      setDeletingDestinationId(destination.destinationId);
      await deleteForwardingDestination(destination.destinationId).unwrap();
      if (forwardingDestinationId === destination.destinationId) {
        setForwardingDestinationId('');
      }
      if (editingDestinationId === destination.destinationId) {
        handleCancelDestinationEdit();
      }
      toast.success('Destination deleted');
      refetchDestinations();
      refetchForwardingSetup?.();
    } catch (error) {
      console.error('Failed to delete forwarding destination:', error);
      setForwardingError(error?.data?.message || error?.message || 'Failed to delete destination');
    } finally {
      setDeletingDestinationId('');
    }
  };

  const handleSaveForwarding = async () => {
    try {
      setForwardingError('');
      if (!domain?.domain) {
        setForwardingError('No domain selected.');
        return;
      }
      if (forwardingEnabled && !forwardingDestinationId) {
        setForwardingError('Select a destination before enabling forwarding.');
        return;
      }

      const saveResponse = await updateDomainForwarding({
        domain: domain.domain,
        forwardingEnabled,
        forwardingDestinationId: forwardingDestinationId || null,
        forwardingPreservePath,
        forwardingPreserveQuery,
        forwardingStatusCode
      }).unwrap();

      const savePayload = saveResponse?.data || saveResponse || {};
      const dnsAutoApply = savePayload?.dnsAutoApply || null;
      const hasBackendAutoApplyResult = dnsAutoApply && typeof dnsAutoApply === 'object' && Object.prototype.hasOwnProperty.call(dnsAutoApply, 'attempted');

      if (forwardingEnabled && canAutoApplyDns && hasBackendAutoApplyResult) {
        if (dnsAutoApply.applied) {
          if (dnsAutoApply?.edge?.status === 'pending_certificate_validation') {
            toast.info(dnsAutoApply?.message || 'Forwarding saved. DNS applied; certificate validation is still propagating.');
          } else {
            toast.success(dnsAutoApply?.message || 'Domain forwarding saved and Route53 DNS was auto-applied');
          }
        } else if (dnsAutoApply.attempted) {
          setForwardingError(dnsAutoApply?.message || 'Forwarding settings were saved, but DNS auto-apply failed.');
          refetchForwardingSetup?.();
          onSaved?.();
          return;
        } else {
          toast.success('Domain forwarding saved');
        }
      } else if (forwardingEnabled && canAutoApplyDns) {
        // Compatibility fallback for older API versions that do not auto-apply on save.
        try {
          await applyDomainForwardingDns({ domain: domain.domain }).unwrap();
          toast.success('Domain forwarding saved and Route53 DNS was auto-applied');
        } catch (dnsError) {
          console.error('Forwarding saved but DNS auto-apply failed:', dnsError);
          setForwardingError(
            `Forwarding settings were saved, but DNS auto-apply failed: ${dnsError?.data?.message || dnsError?.message || 'unknown error'}`
          );
          refetchForwardingSetup?.();
          onSaved?.();
          return;
        }
      } else if (forwardingEnabled && route53AutomationEligible && !redirectInfraConfigured) {
        toast.success('Domain forwarding saved. DNS auto-apply will work after redirect infra is configured.');
      } else {
        toast.success('Domain forwarding saved');
      }

      refetchForwardingSetup?.();
      onSaved?.();
      onHide?.();
    } catch (error) {
      console.error('Failed to save domain forwarding:', error);
      setForwardingError(error?.data?.message || error?.message || 'Failed to save forwarding settings');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem' }}>Domain Forwarding</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="py-2">
          <strong>Domain:</strong> {domain?.domain || '—'}
        </Alert>

        {isLoadingForwardingSetup && (
          <Alert variant="light" className="py-2 d-flex align-items-center gap-2">
            <Spinner size="sm" animation="border" />
            Loading forwarding setup details...
          </Alert>
        )}

        {!isLoadingForwardingSetup && forwardingSetup && (
          <Alert variant={redirectInfraConfigured ? 'success' : 'warning'} className="py-2">
            <div className="mb-2">
              <strong>Forwarding setup</strong>
            </div>
            {redirectInfraConfigured ? (
              <div className="small mb-2">
                Redirect edge is configured at <strong>{setupDistributionDomain}</strong>.
              </div>
            ) : (
              <div className="small mb-2">
                Redirect edge domain is not configured yet. Deploy `kg-redirect` and set
                `REDIRECT_DISTRIBUTION_DOMAIN` in SSM to enable final DNS cutover.
              </div>
            )}
            <div className="small mb-1">
              <strong>Automation:</strong> {forwardingSetup?.automation?.message || 'No automation details available.'}
            </div>
            {forwardingSetup?.forwarding?.destination?.targetUrl && (
              <div className="small mb-1">
                <strong>Current target:</strong> {forwardingSetup.forwarding.destination.targetUrl}
              </div>
            )}
            {(setupApex || setupWww) && (
              <div className="small mt-2">
                <div><strong>Manual DNS records</strong></div>
                {setupApex && (
                  <div>
                    - {setupApex.host} | {setupApex.type} | {setupApex.target}
                  </div>
                )}
                {setupWww && (
                  <div>
                    - {setupWww.host} | {setupWww.type} | {setupWww.target}
                  </div>
                )}
              </div>
            )}
            {setupChecklist.length > 0 && (
              <div className="small mt-2">
                <div><strong>Verify</strong></div>
                {setupChecklist.map((item, idx) => (
                  <div key={`${item}-${idx}`}>- {item}</div>
                ))}
              </div>
            )}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            id="domain-forwarding-enabled"
            label="Enable domain forwarding"
            checked={forwardingEnabled}
            onChange={(e) => setForwardingEnabled(e.target.checked)}
          />
          <Form.Text className="text-muted">
            When enabled, this domain returns an HTTP redirect to the selected destination.
          </Form.Text>
        </Form.Group>

        {forwardingEnabled && (
          <>
            <Form.Group className="mb-3">
              <Form.Label><strong>Forward to destination</strong></Form.Label>
              <Form.Select
                value={forwardingDestinationId}
                onChange={(e) => setForwardingDestinationId(e.target.value)}
                disabled={isLoadingDestinations}
              >
                <option value="">Select destination</option>
                {destinations.map((destination) => (
                  <option key={destination.destinationId} value={destination.destinationId}>
                    {destination.name} ({Number(destination.assignedDomainCount || 0)} domains) - {destination.targetUrl}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {!redirectInfraConfigured && (
              <Alert variant="warning" className="py-2">
                Redirect infra is not fully wired yet. You can save forwarding settings now, but DNS cutover should wait
                until the redirect distribution domain is configured.
              </Alert>
            )}

            {selectedDestination && (
              <Alert variant="light" className="py-2">
                <div><strong>Target:</strong> {selectedDestination.targetUrl}</div>
              </Alert>
            )}

            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                id="domain-forwarding-preserve-path"
                label="Preserve request path"
                checked={forwardingPreservePath}
                onChange={(e) => setForwardingPreservePath(e.target.checked)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="domain-forwarding-preserve-query"
                label="Preserve query string"
                checked={forwardingPreserveQuery}
                onChange={(e) => setForwardingPreserveQuery(e.target.checked)}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label><strong>Redirect type</strong></Form.Label>
              <Form.Select
                value={String(forwardingStatusCode)}
                onChange={(e) => setForwardingStatusCode(Number(e.target.value) === 302 ? 302 : 301)}
              >
                <option value="301">301 (permanent)</option>
                <option value="302">302 (temporary)</option>
              </Form.Select>
            </Form.Group>

            <div className="border rounded p-3">
              <div className="mb-2" style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {editingDestinationId ? 'Edit destination' : 'Add destination'}
              </div>
              <Form.Group className="mb-2">
                <Form.Control
                  placeholder="Destination name (e.g. Main Site)"
                  value={newDestinationName}
                  onChange={(e) => setNewDestinationName(e.target.value)}
                />
              </Form.Group>
              <InputGroup>
                <Form.Control
                  placeholder="https://example.com"
                  value={newDestinationUrl}
                  onChange={(e) => setNewDestinationUrl(e.target.value)}
                />
                <Button
                  variant="outline-primary"
                  onClick={handleCreateOrUpdateDestination}
                  disabled={isCreatingDestination || isUpdatingDestination}
                >
                  {isCreatingDestination || isUpdatingDestination ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <>{editingDestinationId ? 'Save' : <><Plus className="me-1" />Add</>}</>
                  )}
                </Button>
                {editingDestinationId && (
                  <Button variant="outline-secondary" onClick={handleCancelDestinationEdit}>
                    Cancel
                  </Button>
                )}
              </InputGroup>
              <Form.Text className="text-muted">
                HTTPS URLs only.
              </Form.Text>

              {destinations.length > 0 && (
                <div className="mt-3">
                  <div className="mb-2" style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    Manage destinations
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {destinations.map((destination) => (
                      <div
                        key={destination.destinationId}
                        className="border rounded px-2 py-2 d-flex align-items-center justify-content-between"
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{destination.name}</div>
                          <div className="text-muted" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                            {destination.targetUrl}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            Used by {Number(destination.assignedDomainCount || 0)} domain(s)
                            {Array.isArray(destination.assignedDomains) && destination.assignedDomains.length > 0
                              ? `: ${destination.assignedDomains.join(', ')}`
                              : ''}
                          </div>
                        </div>
                        <div className="d-flex gap-2 ms-2">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleEditDestination(destination)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteDestination(destination)}
                            disabled={
                              Number(destination.assignedDomainCount || 0) > 0 ||
                              (isDeletingDestination && deletingDestinationId === destination.destinationId)
                            }
                          >
                            {isDeletingDestination && deletingDestinationId === destination.destinationId ? 'Deleting...' : 'Delete'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {forwardingError && (
          <Alert variant="danger" className="mt-3 mb-0">
            {forwardingError}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveForwarding}
          disabled={isSavingForwarding || isApplyingForwardingDns}
        >
          {isSavingForwarding || isApplyingForwardingDns ? 'Saving...' : 'Save forwarding'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DomainForwardingModal;
